// Story BP-8: linha de usuário com tier/effectiveTier/grandfather/reason
// + CTA pra abrir drawer de override.

import React from 'react';
import { ChevronRight, AlertTriangle, Clock, CreditCard, Shield, Sparkles } from 'lucide-react';
import type { AdminBillingUser } from '../../hooks/useAdminBilling';

export interface AdminBillingUserRowProps {
  user: AdminBillingUser;
  onOpenOverride: (user: AdminBillingUser) => void;
  showReason?: boolean;
}

const TIER_BADGE: Record<string, { label: string; className: string }> = {
  free: { label: 'Free', className: 'bg-neutral-700/50 text-neutral-300 border-neutral-700' },
  pro: { label: 'Pro', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  business: { label: 'Business', className: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
};

const REASON_BADGE: Record<string, { label: string; icon: React.ElementType; className: string }> = {
  grandfather_expiring: {
    label: 'Grandfather expirando',
    icon: Clock,
    className: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  },
  past_due: {
    label: 'Pagamento atrasado',
    icon: CreditCard,
    className: 'bg-red-500/20 text-red-400 border-red-500/30',
  },
};

const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';

export const AdminBillingUserRow: React.FC<AdminBillingUserRowProps> = ({
  user,
  onOpenOverride,
  showReason = false,
}) => {
  const tier = TIER_BADGE[user.effective_tier] ?? TIER_BADGE.free;
  const reason = user.needs_attention_reason ? REASON_BADGE[user.needs_attention_reason] : null;
  const ReasonIcon = reason?.icon;
  const isEffectiveOverride = user.plan_tier !== user.effective_tier;

  return (
    <button
      type="button"
      onClick={() => onOpenOverride(user)}
      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-white/5 bg-neutral-900/40 hover:bg-neutral-900/70 hover:border-white/10 transition-colors text-left"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium text-white truncate">
            {user.full_name || user.email}
          </span>
          {user.is_exempt && (
            <span
              className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20"
              title="Usuário isento (is_exempt=true)"
            >
              <Shield className="w-2.5 h-2.5" /> exempt
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-neutral-500">
          <span className="truncate">{user.email}</span>
          {user.grandfather_until && (
            <span className="shrink-0">
              · grandfather até {formatDate(user.grandfather_until)}
            </span>
          )}
        </div>
      </div>

      {showReason && reason && ReasonIcon && (
        <span
          className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full border shrink-0 ${reason.className}`}
        >
          <ReasonIcon className="w-3 h-3" />
          {reason.label}
        </span>
      )}

      <span
        className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full border shrink-0 ${tier.className}`}
        title={
          isEffectiveOverride
            ? `Raw tier: ${user.plan_tier} · Efetivo: ${user.effective_tier}`
            : undefined
        }
      >
        {isEffectiveOverride && <Sparkles className="w-2.5 h-2.5" />}
        {tier.label}
      </span>

      {user.subscription_status === 'past_due' && !showReason && (
        <span title="Past due" className="shrink-0 inline-flex">
          <AlertTriangle className="w-4 h-4 text-red-400" />
        </span>
      )}

      <ChevronRight className="w-4 h-4 text-neutral-500 shrink-0" />
    </button>
  );
};
